import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';
import React from 'react';

import { BREAKDOWN_REASON_LABELS } from 'utils/breakdown';

import { WA_LOG_TYPES } from '../const';
import { WA_LOG_DETAILS_TEXT } from './text';

import styles from './styles.module.css';

export const formatParamDate = (timestamp, timezone) => {
	if (!timestamp) return null;
	return timezone
		? format(parseISO(timestamp), 'd MMM, HH:mm', { in: tz(timezone) })
		: format(parseISO(timestamp), 'd MMM, HH:mm');
};

export const formatParamTime = (timestamp, timezone) => {
	if (!timestamp) return null;
	return timezone
		? format(parseISO(timestamp), 'HH:mm', { in: tz(timezone) })
		: format(parseISO(timestamp), 'HH:mm');
};

export const getRouteFromParam = (param) => {
	if (!param?.stationFrom) return null;
	return `${param.stationFrom} → ${param.stationTo}`;
};

export const getRouteFromArray = (parameters) => {
	if (!parameters?.length) return null;
	const first = parameters[0];
	const last = parameters[parameters.length - 1];
	if (!first.stationFrom) return null;
	return `${first.stationFrom} → ${last.stationTo}`;
};

export const renderWaSummary = (type, param) => {
	if (!param) return null;

	switch (type) {
		case WA_LOG_TYPES.WA_CREATED:
		case WA_LOG_TYPES.WA_ACTUAL_CREATED: {
			const dateRange =
				param.fromTimestamp && param.toTimestamp
					? `${formatParamDate(param.fromTimestamp, param.timezone)} – ${formatParamDate(param.toTimestamp, param.timezone)}`
					: null;
			return (
				<>
					<span className={styles.summaryBadge}>
						<strong>{param.activityType}</strong>
						{dateRange && <>: {dateRange}</>}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		}
		case WA_LOG_TYPES.WA_STATUS_UPDATED:
			return (
				<>
					<span className={styles.summaryBadge}>
						{param.oldStatus} → {param.newStatus}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		case WA_LOG_TYPES.WA_TIME_UPDATED: {
			const oldFrom = formatParamTime(param.oldFromTime, param.timezone);
			const oldTo = formatParamTime(param.oldToTime, param.timezone);
			const newFrom = formatParamTime(param.newFromTime, param.timezone);
			const newTo = formatParamTime(param.newToTime, param.timezone);
			const fromChanged = oldFrom && newFrom && oldFrom !== newFrom;
			const toChanged = oldTo && newTo && oldTo !== newTo;
			return (
				<>
					<span className={styles.summaryBadge}>
						<strong>{param.activityType}</strong>
						{oldFrom && (
							<>
								:{' '}
								{fromChanged ? (
									<>
										<span className={styles.oldTime}>{oldFrom}</span>
										{newFrom}
									</>
								) : (
									newFrom || oldFrom
								)}
							</>
						)}
						{oldTo && (
							<>
								{' – '}
								{toChanged ? (
									<>
										<span className={styles.oldTime}>{oldTo}</span>
										{newTo}
									</>
								) : (
									newTo || oldTo
								)}
							</>
						)}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		}
		case WA_LOG_TYPES.WA_BREAKDOWN: {
			const reasonLabel = param.reason && (BREAKDOWN_REASON_LABELS[param.reason] ?? param.reason);
			return (
				<>
					{reasonLabel && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.breakdownReason}</strong> {reasonLabel}
						</span>
					)}
					{param.note && <span>{param.note}</span>}
				</>
			);
		}
		default:
			return null;
	}
};

export const isWaLogType = (type) => Object.values(WA_LOG_TYPES).includes(type);

export const renderParamRow = (type, param) => {
	const route = `${param.stationFrom} → ${param.stationTo}`;
	const timeRange =
		param.fromTimestamp && param.toTimestamp
			? `${formatParamTime(param.fromTimestamp, param.timezone)} – ${formatParamTime(param.toTimestamp, param.timezone)}`
			: null;

	switch (type) {
		case WA_LOG_TYPES.WA_CREATED:
			return (
				<>
					<span className={styles.summaryBadge}>{param.activityType}</span>
					<span>
						{route}
						{timeRange && <> : {timeRange}</>}
					</span>
				</>
			);
		case WA_LOG_TYPES.WA_ACTUAL_CREATED:
			return (
				<>
					{param.activityType && <span className={styles.summaryBadge}>{param.activityType}</span>}
					{param.timezone && <span className={styles.timezoneBadge}>TZ: {param.timezone}</span>}
					<span>
						{route}
						{timeRange && <> : {timeRange}</>}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		case WA_LOG_TYPES.WA_STATUS_UPDATED:
			return (
				<>
					<span className={styles.summaryBadge}>
						{param.oldStatus} → {param.newStatus}
					</span>
					{param.timezone && <span className={styles.timezoneBadge}>TZ: {param.timezone}</span>}
					<span>
						{route}
						{timeRange && <> : {timeRange}</>}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		case WA_LOG_TYPES.WA_TIME_UPDATED: {
			const oldFrom = formatParamTime(param.oldFromTime, param.timezone);
			const oldTo = formatParamTime(param.oldToTime, param.timezone);
			const newFrom = formatParamTime(param.newFromTime, param.timezone);
			const newTo = formatParamTime(param.newToTime, param.timezone);
			const fromChanged = oldFrom && newFrom && oldFrom !== newFrom;
			const toChanged = oldTo && newTo && oldTo !== newTo;
			return (
				<>
					{param.status && <span className={styles.summaryBadge}>{param.status}</span>}
					{param.activityType && <span className={styles.summaryBadge}>{param.activityType}</span>}
					{param.timezone && <span className={styles.timezoneBadge}>TZ: {param.timezone}</span>}
					<span>
						{route}
						{oldFrom && (
							<>
								{' : '}
								{fromChanged ? (
									<>
										<span className={styles.oldTime}>{oldFrom}</span>
										{newFrom}
									</>
								) : (
									newFrom || oldFrom
								)}
							</>
						)}
						{oldTo && (
							<>
								{' – '}
								{toChanged ? (
									<>
										<span className={styles.oldTime}>{oldTo}</span>
										{newTo}
									</>
								) : (
									newTo || oldTo
								)}
							</>
						)}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		}
		case WA_LOG_TYPES.WA_BREAKDOWN:
			return (
				<>
					{param.activityType && <span className={styles.summaryBadge}>{param.activityType}</span>}
					<span>
						{route}
						{timeRange && <> : {timeRange}</>}
					</span>
					{param.reason && (
						<span className={styles.summaryBadge}>
							<strong>{WA_LOG_DETAILS_TEXT.reason}</strong> {param.reason}
						</span>
					)}
				</>
			);
		default:
			return route;
	}
};
